import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraInlineBanner, SpecraSectionHeader, SpecraSegmentedControl, SpecraText, SpecraTimeline } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LifecycleNodeKind, LinkedChips } from '../../../../components/LinkedChips';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { useGrowthThreadEnabled } from '../../../GrowthHub/growthFlags';
import { ClaimAudienceControl } from '../../../Registries/hearsay/ClaimAudienceControl';
import { useInvalidateHearsay, usePursuitHearsay } from '../hearsay/pursuitHearsay';
import { useThread } from '../pursuitHooks';
const FILTERS = [
    {
        value: 'all',
        label: 'All'
    },
    {
        value: 'market',
        label: 'Market'
    },
    {
        value: 'customer',
        label: 'Customer'
    },
    {
        value: 'shaping',
        label: 'Shaping'
    },
    {
        value: 'decisions',
        label: 'Decisions'
    }
];
function isFilter(value) {
    return FILTERS.some((f)=>f.value === value);
}
/** Newest-first events → one group per calendar year (the order the API returns is kept). */ export function groupByYear(events, toEntry) {
    const groups = [];
    for (const event of events){
        const year = event.occurredAt ? String(parseISO(event.occurredAt).getFullYear()) : 'Undated';
        let group = groups.find((g)=>g.id === year);
        if (!group) {
            group = {
                id: year,
                label: year,
                items: []
            };
            groups.push(group);
        }
        group.items.push(toEntry(event));
    }
    return groups;
}
function entry(captureProjectId, event, hearsayActions) {
    const links = event.links.filter((l)=>l.href).map((l)=>({
            id: `${l.kind}:${l.id}`,
            label: l.label,
            href: l.href
        }));
    if (event.kind === 'go_no_go') {
        links.push({
            id: `decision:${event.sourceId}`,
            label: 'Decision record',
            href: `/capture/${captureProjectId}/decisions?record=${encodeURIComponent(event.sourceId)}`
        });
    }
    if (event.kind === 'win_theme') {
        links.push({
            id: 'win-themes',
            label: 'Win themes',
            href: `/capture/${captureProjectId}/win-themes`
        });
    }
    return {
        id: event.id,
        date: event.occurredAt ? format(parseISO(event.occurredAt), 'd MMM') : undefined,
        badge: /*#__PURE__*/ _jsx(SpecraBadge, {
            variant: event.tone || 'neutral',
            children: event.kindLabel
        }),
        title: event.title,
        description: event.masked ? /*#__PURE__*/ _jsx(SpecraText, {
            tone: "muted",
            children: "Details need sensitive access"
        }) : event.body || undefined,
        links,
        // GRW-20: a hearsay item carries REG's audience chip, found by source_id.
        actions: event.kind === 'hearsay' ? hearsayActions?.(event) : undefined
    };
}
/**
 * The pursuit THREAD on Home (GRW-16 item 4), behind growth_thread_v1. Off:
 * the record's linked records instead (today's chips).
 */ export function PursuitThread({ captureProjectId }) {
    const threadOn = useGrowthThreadEnabled();
    if (!threadOn) {
        return /*#__PURE__*/ _jsxs("section", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                    title: "Linked records",
                    variant: "group"
                }),
                /*#__PURE__*/ _jsx(LinkedChips, {
                    id: captureProjectId,
                    kind: LifecycleNodeKind.PURSUIT
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(Thread, {
        captureProjectId: captureProjectId
    });
}
function Thread({ captureProjectId }) {
    const [params, setParams] = useSearchParams();
    const raw = params.get('thread');
    const filter = isFilter(raw) ? raw : 'all';
    const thread = useThread(captureProjectId, filter);
    const hearsay = usePursuitHearsay(captureProjectId);
    const invalidateHearsay = useInvalidateHearsay();
    const pages = thread.data?.pages ?? [];
    const events = useMemo(()=>pages.flatMap((p)=>p.events), [
        pages
    ]);
    const unavailable = useMemo(()=>[
            ...new Set(pages.flatMap((p)=>p.unavailableSources))
        ], [
        pages
    ]);
    const groups = useMemo(()=>{
        const claims = new Map((hearsay.data?.claims ?? []).map((c)=>[
                c.id,
                c
            ]));
        // A claim not (yet) in the cache renders without a chip, never a placeholder.
        const actions = (e)=>{
            const claim = claims.get(e.sourceId);
            return claim ? /*#__PURE__*/ _jsx(ClaimAudienceControl, {
                claim: claim,
                "data-testid": Testids.pursuitHome.threadAudience,
                onChanged: invalidateHearsay,
                size: "xs"
            }) : undefined;
        };
        return groupByYear(events, (e)=>entry(captureProjectId, e, actions));
    }, [
        events,
        captureProjectId,
        hearsay.data?.claims,
        invalidateHearsay
    ]);
    const setFilter = (next)=>{
        const updated = new URLSearchParams(params);
        if (next === 'all') updated.delete('thread');
        else updated.set('thread', next);
        setParams(updated, {
            replace: true
        });
    };
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: "Thread",
                variant: "group"
            }),
            /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "Filter thread",
                onValueChange: setFilter,
                options: FILTERS,
                size: "sm",
                testId: Testids.pursuitHome.threadFilter,
                value: filter
            }),
            unavailable.length > 0 && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "warning",
                variant: "framed",
                children: [
                    "Some history could not be loaded (",
                    unavailable.join(', '),
                    "). Showing the rest."
                ]
            }),
            thread.isLoading ? /*#__PURE__*/ _jsx(SpecraText, {
                tone: "muted",
                children: "Loading the thread…"
            }) : /*#__PURE__*/ _jsx(SpecraTimeline, {
                emptyText: "Nothing on this thread yet. Log a touchpoint or link an RFI response.",
                getItemTestId: ()=>Testids.pursuitHome.threadItem,
                groups: groups,
                renderLink: renderRouterLink,
                variant: "dated"
            }),
            thread.hasNextPage && /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.pursuitHome.threadMore,
                disabled: thread.isFetchingNextPage,
                onClick: ()=>void thread.fetchNextPage(),
                variant: "secondary",
                children: "Show older"
            })
        ]
    });
}
